import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    applyChange,
    buildRows,
    DynamicMode,
    Edits,
    ItemEdit,
    ProductItem,
    refreshEdits,
    SourceItem
} from "./transferProductRows";
import {Product} from "../../../../api/Products";

export type TransferChangeFx = (e: ItemEdit, row: ProductItem) => ItemEdit;

export type TransferEdits = ReturnType<typeof useTransferEdits>;

export function useTransferEdits(items: SourceItem[], reload: () => void) {
    const [mode, setModeState] = useState<DynamicMode>("grouped");
    const [edits, setEdits] = useState<Edits>(() => new Map());
    const [error, setError] = useState("");
    const [conflictKeys, setConflictKeys] = useState<Set<string>>(() => new Set());

    // read by change() so back-to-back changes don't apply against a stale render
    const editsRef = useRef(edits);
    const rowsRef = useRef<ProductItem[]>([]);
    const itemsRef = useRef(items);
    const savedProductRef = useRef<Product | null>(null);

    useEffect(() => {
        editsRef.current = refreshEdits(editsRef.current, itemsRef.current, items, savedProductRef.current);
        itemsRef.current = items;
        savedProductRef.current = null;
        setEdits(editsRef.current);
        setError("");
        setConflictKeys(new Set());
    }, [items]);

    // keeps row ids stable across rebuilds so the table keeps its selection
    const idMap = useRef(new Map<string, number>());
    const idFor = useCallback((key: string) => {
        let id = idMap.current.get(key);
        if(!id) {
            id = idMap.current.size + 1;
            idMap.current.set(key, id);
        }

        return id;
    }, []);

    const rows = useMemo(() => buildRows(items, edits, mode, idFor), [items, edits, mode, idFor]);
    rowsRef.current = rows;

    const change = useCallback((changed: ProductItem[], fx: TransferChangeFx) => {
        const result = applyChange(rowsRef.current, editsRef.current, itemsRef.current, changed, fx);
        if("error" in result) {
            setError(result.error);
            setConflictKeys(result.conflictKeys);
            return false;
        }

        editsRef.current = result.edits;
        setEdits(result.edits);
        setError("");
        setConflictKeys(new Set());
        return true;
    }, []);

    // conflict keys belong to the previous mode's rows
    const setMode = useCallback((value: DynamicMode) => {
        setModeState(value);
        setError("");
        setConflictKeys(new Set());
    }, []);

    const reloadRef = useRef(reload);
    reloadRef.current = reload;

    const productSaved = useCallback((product: Product) => {
        savedProductRef.current = product;
        reloadRef.current();
    }, []);

    return {
        mode,
        setMode,
        rows,
        edits,
        error,
        conflictKeys,
        change,
        productSaved,
    };
}
